import React, { useRef, useState, useCallback } from 'react';
import {
  Overlay,
  ModalCard,
  TopRow,
  ModalTitle,
  ModalDescription,
  CloseButton,
  Divider,
  PlansRow,
  PlansLabel,
  PlanBadge,
  AllPlansText,
  ANIMATION_DURATION,
  SheetHeader,
  SheetTopBar,
  SheetTitleRow,
  SheetTitle,
  SheetCloseButton,
  SheetBottomBar,
  SheetContent,
  SheetDescription,
  SheetDivider,
  DesktopPlansRow,
} from './index.styles';
import type { EntitlementInfoModalProps, PlanEntry } from '../../types';
import nebulaTheme from '@nebula/theme';
import useDialogFocus from './useDialogFocus';

const ALL_PLAN_IDS = ['basic', 'identity', 'credit_expert'] as const;

const PLAN_MAP: Record<string, PlanEntry> = {
  basic: { name: 'Basic', color: nebulaTheme.color.ui.grey[90] },
  identity: { name: 'Identity Plus', color: nebulaTheme.color.decorative.purple[90] },
  credit_expert: { name: 'CreditExpert', color: nebulaTheme.color.decorative.blue[90] },
};

const EntitlementInfoModal: React.FC<EntitlementInfoModalProps> = ({ feature, onClose }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [closing, setClosing] = useState(false);

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      onClose();
    }, ANIMATION_DURATION);
  }, [onClose]);

  useDialogFocus(modalRef, handleClose);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      handleClose();
    }
  };

  const { info, values } = feature;

  if (!info) return null;

  const resolvedPlanIds = info.qualifierPlans || ALL_PLAN_IDS.filter(id => values[id]?.included);
  const isAllPlans = resolvedPlanIds.length === ALL_PLAN_IDS.length;
  const planBadges = isAllPlans ? [] : resolvedPlanIds.map(p => PLAN_MAP[p]).filter(Boolean);

  return (
    <Overlay
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="feature-modal-title"
      closing={closing}
    >
      <ModalCard ref={modalRef} closing={closing} id={`${feature.id}`} tabIndex={-1}>
        <SheetHeader>
          <SheetTopBar />
          <SheetTitleRow>
            <SheetTitle id="feature-modal-title-mobile">{feature.label}</SheetTitle>
            <SheetCloseButton onClick={handleClose} aria-label="Close" type="button">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M9 3L3 9M3 3L9 9"
                  stroke="#252C34"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </SheetCloseButton>
          </SheetTitleRow>
          <SheetBottomBar />
        </SheetHeader>

        <SheetContent>
          <SheetDescription>{info.description}</SheetDescription>
          <SheetDivider />
          <PlansRow>
            <PlansLabel>{info.qualifier}:</PlansLabel>
            {isAllPlans ? (
              <AllPlansText>All accounts</AllPlansText>
            ) : (
              planBadges.map(plan => (
                <PlanBadge key={`mobile-${plan.name}`} color={plan.color}>
                  {plan.name}
                </PlanBadge>
              ))
            )}
          </PlansRow>
        </SheetContent>

        <TopRow>
          <ModalTitle id="feature-modal-title">{feature.label}</ModalTitle>
          <ModalDescription>{info.description}</ModalDescription>
          <CloseButton onClick={handleClose} aria-label="Close" type="button" id="entitlement-info-modal-close-btn">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 4L4 12M4 4L12 12"
                stroke="#252C34"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </CloseButton>
        </TopRow>
        <Divider />
        <DesktopPlansRow>
          <PlansLabel>{info.qualifier}:</PlansLabel>
          {isAllPlans ? (
            <AllPlansText>All accounts</AllPlansText>
          ) : (
            planBadges.map(plan => (
              <PlanBadge key={plan.name} color={plan.color}>
                {plan.name}
              </PlanBadge>
            ))
          )}
        </DesktopPlansRow>
      </ModalCard>
    </Overlay>
  );
};

export default EntitlementInfoModal;
